import { CellState } from './types';

const BASE_PATH = import.meta.env.BASE_URL.replace(/\/$/, '');

/* ---------- Public types ---------- */

export interface ShareParams {
  solution: boolean[][];
  grid?: CellState[][];
}

export interface DecodedShare {
  puzzle: { width: number; height: number; solution: boolean[][] };
  progress?: CellState[][];
}

/* ---------- Compression helpers (CompressionStream API) ---------- */

async function compress(data: Uint8Array): Promise<Uint8Array> {
  const cs = new CompressionStream('deflate');
  const writer = cs.writable.getWriter();
  writer.write(data);
  writer.close();
  const chunks: Uint8Array[] = [];
  const reader = cs.readable.getReader();
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
  }
  const total = chunks.reduce((s, c) => s + c.length, 0);
  const result = new Uint8Array(total);
  let off = 0;
  for (const c of chunks) {
    result.set(c, off);
    off += c.length;
  }
  return result;
}

async function decompress(data: Uint8Array): Promise<Uint8Array> {
  const ds = new DecompressionStream('deflate');
  const writer = ds.writable.getWriter();
  writer.write(data);
  writer.close();
  const chunks: Uint8Array[] = [];
  const reader = ds.readable.getReader();
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
  }
  const total = chunks.reduce((s, c) => s + c.length, 0);
  const result = new Uint8Array(total);
  let off = 0;
  for (const c of chunks) {
    result.set(c, off);
    off += c.length;
  }
  return result;
}

/* ---------- Base64url helpers ---------- */

function toBase64Url(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

function fromBase64Url(str: string): Uint8Array {
  let b64 = str.replace(/-/g, '+').replace(/_/g, '/');
  while (b64.length % 4 !== 0) b64 += '=';
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/* ---------- Binary packing ---------- */
// Layout:
//   [0]    rows
//   [1]    cols
//   [2]    flags  (bit 0 = has progress)
//   [3..S] solution — 1 bit per cell, ceil(rows*cols / 8) bytes
//   [S..P] progress — 2 bits per cell, ceil(rows*cols*2 / 8) bytes (only if flag)

const CELL_BITS: Record<string, number> = {
  [CellState.Empty]: 0b00,
  [CellState.Filled]: 0b01,
  [CellState.Marked]: 0b10,
};

const BITS_CELL: CellState[] = [
  CellState.Empty,
  CellState.Filled,
  CellState.Marked,
];

export function packPuzzle(
  solution: boolean[][],
  progress?: CellState[][],
): Uint8Array {
  const rows = solution.length;
  const cols = solution[0]?.length ?? 0;
  const cellCount = rows * cols;
  const solutionBytes = Math.ceil(cellCount / 8);
  const hasProgress =
    progress != null &&
    progress.some((row) => row.some((c) => c !== CellState.Empty));
  const progressBytes = hasProgress ? Math.ceil((cellCount * 2) / 8) : 0;

  const buf = new Uint8Array(3 + solutionBytes + progressBytes);
  buf[0] = rows;
  buf[1] = cols;
  buf[2] = hasProgress ? 1 : 0;

  // Solution: 1 bit per cell
  for (let i = 0; i < cellCount; i++) {
    if (solution[Math.floor(i / cols)][i % cols]) {
      buf[3 + Math.floor(i / 8)] |= 1 << (i % 8);
    }
  }

  // Progress: 2 bits per cell
  if (hasProgress && progress) {
    const off = 3 + solutionBytes;
    for (let i = 0; i < cellCount; i++) {
      const bits = CELL_BITS[progress[Math.floor(i / cols)][i % cols]] ?? 0;
      const bitOff = i * 2;
      buf[off + Math.floor(bitOff / 8)] |= bits << (bitOff % 8);
    }
  }

  return buf;
}

export function unpackPuzzle(buf: Uint8Array): DecodedShare | null {
  if (buf.length < 3) return null;
  const rows = buf[0];
  const cols = buf[1];
  if (rows === 0 || cols === 0) return null;
  const hasProgress = (buf[2] & 1) !== 0;
  const cellCount = rows * cols;
  const solutionBytes = Math.ceil(cellCount / 8);

  // Solution
  const solution: boolean[][] = [];
  for (let r = 0; r < rows; r++) {
    const row: boolean[] = [];
    for (let c = 0; c < cols; c++) {
      const i = r * cols + c;
      const byteIdx = 3 + Math.floor(i / 8);
      const bit = byteIdx < buf.length ? (buf[byteIdx] >> (i % 8)) & 1 : 0;
      row.push(bit === 1);
    }
    solution.push(row);
  }

  // Progress
  let progress: CellState[][] | undefined;
  if (hasProgress) {
    const off = 3 + solutionBytes;
    progress = [];
    for (let r = 0; r < rows; r++) {
      const row: CellState[] = [];
      for (let c = 0; c < cols; c++) {
        const i = r * cols + c;
        const bitOff = i * 2;
        const byteIdx = off + Math.floor(bitOff / 8);
        if (byteIdx < buf.length) {
          const bits = (buf[byteIdx] >> (bitOff % 8)) & 0b11;
          row.push(bits < BITS_CELL.length ? BITS_CELL[bits] : CellState.Empty);
        } else {
          row.push(CellState.Empty);
        }
      }
      progress.push(row);
    }
  }

  return {
    puzzle: { width: cols, height: rows, solution },
    progress,
  };
}

/* ---------- URL building (async — CompressionStream) ---------- */

export async function url(params: ShareParams): Promise<string> {
  const packed = packPuzzle(params.solution, params.grid);
  const compressed = await compress(packed);
  const encoded = toBase64Url(compressed);
  return `${window.location.origin}${BASE_PATH}/shared?d=${encoded}`;
}

/* ---------- URL decoding (async) ---------- */

export async function decode(search: string): Promise<DecodedShare | null> {
  try {
    const sp = new URLSearchParams(search);
    const d = sp.get('d');
    if (!d) return null;
    const compressed = fromBase64Url(d);
    const packed = await decompress(compressed);
    return unpackPuzzle(packed);
  } catch {
    return null;
  }
}

/* ---------- Share / Copy ---------- */

export async function shareOrCopy(
  shareUrl: string,
): Promise<'shared' | 'copied' | 'failed'> {
  const isMobile =
    typeof navigator !== 'undefined' &&
    /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  if (isMobile && navigator.share) {
    try {
      await navigator.share({ url: shareUrl });
      return 'shared';
    } catch {
      // User cancelled or share failed — fall through to clipboard
    }
  }

  try {
    await navigator.clipboard.writeText(shareUrl);
    return 'copied';
  } catch {
    return 'failed';
  }
}
